import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, MessageSquare, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id') || 'N/A';

  return (
    <div className="app-container">
      <Navbar />
      
      {/* Success Hero Section */}
      <section className="min-h-screen flex items-center justify-center py-24 px-6">
        <div className="max-w-2xl w-full">
          {/* Success Icon */}
          <div className="flex justify-center mb-8">
            <div className="bg-green-500/20 border-2 border-green-500 rounded-full p-6">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold uppercase mb-4">
              <span className="text-green-500">PAYMENT</span> SUCCESSFUL!
            </h1>
            <p className="text-gray-400 text-lg mb-4">
              Your order has been confirmed and we're ready to get started.
            </p>
            {orderId !== 'N/A' && (
              <p className="text-sm text-gray-500">
                Order ID: <span className="text-white font-mono">{orderId}</span>
              </p>
            )}
          </div>

          {/* Important Next Steps */}
          <div className="bg-[#0A0A0A] border-2 border-red-500 p-8 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <MessageSquare className="w-12 h-12 text-red-500" />
              <div>
                <h2 className="text-2xl md:text-3xl font-bold uppercase text-white">
                  IMPORTANT: NEXT STEPS
                </h2>
                <p className="text-gray-400 text-sm">Complete your order by following these steps</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-500 text-white font-bold flex items-center justify-center text-xl">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Join Our Discord Server</h3>
                  <p className="text-gray-300 mb-4">
                    Click the button below to join our Discord community where all order fulfillment happens.
                  </p>
                  <a
                    href="https://discord.gg/afkrev"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold py-4 px-8 uppercase tracking-widest transition-all text-lg group"
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    JOIN DISCORD SERVER
                    <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-500 text-white font-bold flex items-center justify-center text-xl">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Open a Support Ticket</h3>
                  <p className="text-gray-300">
                    Once you've joined, navigate to the <span className="text-red-500 font-bold">#open-ticket</span> channel and create a new ticket. Our team will assist you with your order immediately.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-500 text-white font-bold flex items-center justify-center text-xl">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Provide Order Details</h3>
                  <p className="text-gray-300">
                    Share your order confirmation email or order ID in the ticket so we can quickly process your request.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="bg-black/40 border border-white/10 p-6 mb-8">
            <h3 className="text-lg font-bold text-white mb-3">📧 CHECK YOUR EMAIL</h3>
            <p className="text-gray-300 text-sm">
              You should receive an order confirmation email shortly. If you don't see it, check your spam folder.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/"
              className="flex-1 text-center bg-black/60 border border-white/20 hover:border-red-500 text-white font-bold py-4 px-8 uppercase tracking-wider transition-all"
            >
              RETURN HOME
            </Link>
            <Link
              to="/portal"
              className="flex-1 text-center bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 uppercase tracking-wider transition-all"
            >
              VIEW MY ORDERS
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
